import React, { useState } from "react";

import { filter, find, includes, isEmpty, map, reject, some } from "lodash/fp";

import { Autocomplete, TextField, Typography } from "@mui/material";

import useWorkspaceTeam from "@app/analytics/account/WorkspaceSettings/hooks/useWorkspaceTeam";
import BasicModal, { ModalProps } from "@app/analytics/components/BasicModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import { WorkspaceUserInviteBody } from "@app/common/account/api";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { toast } from "@app/common/hooks/useToasts";
import { getInitials, pluralizePhrase } from "@app/common/utils";
import {
  EnterpriseTeam,
  EnterpriseWorkspaceTeam,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import { Column } from "@rui/Stack";

import AddWorkspaceMemberConfirmModal from "./AddWorkspaceMemberConfirmModal";
import MissingMemberAutocompleteOption from "./MissingMemberAutocompleteOption";
import TeamMemberAutocompleteOption from "./TeamMemberAutocompleteOption";
import TeamMemberAutocompleteTag from "./TeamMemberAutocompleteTag";

const getOptionLabel = (option: AutocompleteOption) =>
  option.fullName ?? option.email;

const matchesEmail = (userEmail: string, searchTerm: string) => {
  if (searchTerm.includes("@")) {
    return userEmail.includes(searchTerm);
  }

  const userEmailWithoutDomain = userEmail.replace(/@.+/, "");
  return userEmailWithoutDomain.includes(searchTerm);
};

const filterUsers = (
  users: AutocompleteOption[],
  state: {
    inputValue: string;
    getOptionLabel: (user: AutocompleteOption) => string;
  }
) => {
  const caseInsensitiveValue = state.inputValue.toLowerCase().trim();
  return users.filter(
    (user) =>
      matchesEmail(user.email.toLowerCase(), caseInsensitiveValue) ||
      state.getOptionLabel(user).toLowerCase().includes(caseInsensitiveValue)
  );
};

const isOnTeam = (
  team: EnterpriseTeam | EnterpriseWorkspaceTeam | null,
  option: AutocompleteOption
) => {
  // TODO (ENG-11564): handle a pending invite here as well
  if (!team || !option.userId) return false;
  return some({ userId: option.userId }, team.users);
};

interface AddTeamMembersToModalProps extends ModalProps {
  team: EnterpriseWorkspaceTeam | EnterpriseTeam | null;
  visible?: boolean;
}

export interface AutocompleteOption {
  type: "user" | "invite";
  email: string;
  fullName?: string;
  initials: string | null;
  userId?: string;
  teams: EnterpriseWorkspaceTeam[];
}

const AddTeamMembersModal = ({
  onClose,
  open,
  team,
  visible = true,
}: AddTeamMembersToModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const [workspaceInviteModalOpen, setWorkspaceInviteModalOpen] =
    useState(false);
  const [sendNewTeamMemberInvites, setSendNewTeamMemberInvites] =
    useLocalStorage("sendNewTeamMemberInvites", false);

  const [autocompleteMenuOpen, setAutocompleteMenuOpen] = useState(false);
  const [textInputValue, setTextInputValue] = useState<string>("");
  const [selectedPeople, setSelectedPeople] = useState<AutocompleteOption[]>(
    []
  );

  const { licenses } = useBillingContext();
  const { workspace } = useWorkspace();
  const { addUsers, addingUsers: isLoading } = useWorkspaceTeam(team?.id);

  // Converts both of the invites and the users into the same type
  const userAutocompleteOptions: AutocompleteOption[] = workspace.users.map(
    (user) => ({
      type: "user",
      email: user.email,
      fullName: user.fullName,
      initials: user.initials,
      userId: user.userId,
      teams: user.teams ?? [],
    })
  );

  const inviteAutocompleteOptions: AutocompleteOption[] = workspace.invites.map(
    (invite) => ({
      type: "invite",
      email: invite.email,
      initials: getInitials(invite.email) ?? "",
      teams: [],
    })
  );

  const alreadySelectedEmails = map("email", selectedPeople);

  const autocompleteOptions = [
    ...userAutocompleteOptions,
    ...inviteAutocompleteOptions,
  ];

  const existingMembers = workspace.users.filter((user) => {
    if (find({ userId: user.userId }, selectedPeople)) return user;
  });

  const handleAutoSelectFromTextInput = () => {
    if (!textInputValue) return;

    const matchingPeople = filterUsers(autocompleteOptions, {
      inputValue: textInputValue,
      getOptionLabel,
    }).filter((user) => !alreadySelectedEmails.includes(user.email));

    if (matchingPeople.length === 1) {
      setSelectedPeople((current) => [...current, matchingPeople[0]]);
      handleTextFieldChange("");
    }
  };

  const handleAddTeamMembers = async () => {
    // if the menu is open, the "submit" happening here is just the user selecting
    // an option by hitting "enter" on an option. we don't want to submit in this case
    if (autocompleteMenuOpen || error || !team) return;

    if (isEmpty(selectedPeople)) {
      setError("Must select at least one member");
      return;
    }

    const newMembers = map("email", filter({ type: "invite" }, selectedPeople));

    const invites: WorkspaceUserInviteBody[] = newMembers.map((email) => ({
      email,
      teamId: team.id,
      teamRole: "member",
    }));

    const response = await addUsers({
      workspaceId: workspace.id,
      invites,
      users: existingMembers,
    });

    let errorMessage = "";
    let updatedSelectedPeople = selectedPeople;

    if (response.failures.users.length) {
      const failedUpdateUserIds = map("userId", response.failures.users);
      // remove any people from the input that were successfully added to the team
      updatedSelectedPeople = filter(
        (p) => !p.userId || failedUpdateUserIds.includes(p.userId),
        updatedSelectedPeople
      );

      errorMessage = `Unable to add users to ${team.name}`;
    }

    if (response.failures.emails.length) {
      // remove any emails from the input that were successfully invited to the workspace
      updatedSelectedPeople = reject(
        (p) =>
          p.type === "invite" && !includes(p.email, response.failures.emails),
        updatedSelectedPeople
      );
      errorMessage = `Unable to ${response.failures.users.length ? "add or invite" : "invite"} users to ${team.name}`;
      if (response.errorCodes.includes(402)) {
        errorMessage +=
          ". Purchase additional licenses to add more members to your Workspace.";
      }
    }

    if (response.numInvitesSent > 0) {
      toast.success(
        `${pluralizePhrase(response.numInvitesSent, "member")} added to ${team.name}`
      );
    }

    if (errorMessage) {
      setSelectedPeople(updatedSelectedPeople);
      setError(errorMessage);
      return;
    }

    return handleClose();
  };

  const handleClose = () => {
    onClose();
    setWorkspaceInviteModalOpen(false);
    setSelectedPeople([]);
    setError(null);
  };

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    value: AutocompleteOption[]
  ) => {
    if (error) {
      setError(null);
    }
    setSelectedPeople(value);
    handleTextFieldChange("");
  };

  const handleTextFieldChange = (newValue: string) => {
    setTextInputValue(newValue);
    if (!newValue) {
      setError(null);
    }
  };

  const handleAutocompleteInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => handleTextFieldChange(event.target.value);

  const handleTextFieldKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (error) {
      setError(null);
    }

    switch (event.key) {
      case "Enter":
        event.preventDefault();
        return handleAddTeamMembers();
      case "Escape":
        handleTextFieldChange("");
        break;
      default:
        break;
    }
  };

  const handleSelectNewInvite = (email: string) => {
    const selectedPerson: AutocompleteOption = {
      type: "invite",
      email,
      initials: getInitials(email) ?? "",
      teams: [],
    };

    setSelectedPeople([...selectedPeople, selectedPerson]);
    handleTextFieldChange("");

    if (workspaceInviteModalOpen) {
      setWorkspaceInviteModalOpen(false);
    }
  };

  const handleWorkspaceInviteModalOpen = async () => {
    if (sendNewTeamMemberInvites) {
      return handleSelectNewInvite(textInputValue);
    }

    return setWorkspaceInviteModalOpen(true);
  };

  const numUsersToInvite = selectedPeople.length - existingMembers.length;
  const mustPurchaseAdditionalLicenses =
    licenses.numUnclaimed === 0 || numUsersToInvite >= licenses.numUnclaimed;

  return (
    <>
      <AddWorkspaceMemberConfirmModal
        email={textInputValue}
        loading={isLoading}
        onClose={() => setWorkspaceInviteModalOpen(false)}
        onConfirm={() => handleSelectNewInvite(textInputValue)}
        open={!error && workspaceInviteModalOpen}
        setSendNewTeamMemberInvites={setSendNewTeamMemberInvites}
      />
      <BasicModal
        loading={isLoading}
        modalSx={{ visibility: visible ? "visible" : "hidden" }}
        onClose={handleClose}
        onSubmit={handleAddTeamMembers}
        open={open}
        cancelText="I'll do this later"
        submitText="Add"
        title="Add Team Members"
        sx={{ width: "500px" }}
      >
        <Typography variant="bodyPrimaryReg" color="shades.neutral.700" mb={3}>
          {team?.name}
        </Typography>
        <Typography
          variant="bodyPrimaryMed"
          color="shades.neutral.700"
          mb={1.5}
        >
          Enter name or email
        </Typography>
        <Column spacing={1}>
          <form onSubmit={handleAddTeamMembers}>
            <Autocomplete
              autoComplete
              clearOnEscape
              clearOnBlur={false}
              filterSelectedOptions
              inputValue={textInputValue}
              multiple
              color="primary"
              popupIcon={null}
              filterOptions={filterUsers}
              options={autocompleteOptions}
              isOptionEqualToValue={(option, value) =>
                option.email === value.email
              }
              getOptionLabel={getOptionLabel}
              onOpen={() => setAutocompleteMenuOpen(true)}
              onClose={() => setAutocompleteMenuOpen(false)}
              onBlur={handleAutoSelectFromTextInput}
              noOptionsText={
                <MissingMemberAutocompleteOption
                  value={textInputValue}
                  error={error}
                  onSubmit={handleWorkspaceInviteModalOpen}
                  alreadySelected={alreadySelectedEmails.includes(
                    textInputValue
                  )}
                  purchaseRequired={mustPurchaseAdditionalLicenses}
                  loading={isLoading}
                />
              }
              renderInput={(props) => (
                <TextField
                  {...props}
                  error={Boolean(error)}
                  helperText={error}
                  placeholder={
                    isEmpty(selectedPeople)
                      ? "Start typing a name or email"
                      : undefined
                  }
                  onKeyDown={handleTextFieldKeyDown}
                  onChange={handleAutocompleteInputChange}
                  disabled={isLoading}
                />
              )}
              renderOption={(props, option) => (
                <TeamMemberAutocompleteOption
                  {...props}
                  option={option}
                  onTeam={isOnTeam(team, option)}
                />
              )}
              renderTags={(tags, getTagProps) =>
                tags.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <TeamMemberAutocompleteTag
                      key={key}
                      {...tagProps}
                      name={getOptionLabel(option)}
                    />
                  );
                })
              }
              selectOnFocus
              value={selectedPeople}
              onChange={handleAutocompleteChange}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  py: 0.5,
                  input: { fontSize: "1rem" },
                },
              }}
            />
          </form>
        </Column>
      </BasicModal>
    </>
  );
};

export default AddTeamMembersModal;
